import { useTranslation } from 'react-i18next';
import { Relative, ReviewPathology, PathologyHistory } from '@myriadgenetics/mgh-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import PathologyDetails from '../pathology-details';

import './index.scss';
import { Text1 } from '../../../elements/typography';
import { isSimonMedUrl } from '../../../helpers/url';
import Button from '../../../elements/button';

interface Props {
  pathologies: ReviewPathology[] | PathologyHistory[];
  relative: Relative | null;
  onEditClicked?: (pathology: PathologyHistory | ReviewPathology, index: number) => void;
  onDeleteClicked?: (pathology: ReviewPathology | PathologyHistory, index: number) => void;
  onAddClicked?: () => void;
  num?: number;
}

function CancerHistoryCard({ pathologies, onAddClicked, onEditClicked, onDeleteClicked, relative, num }: Props) {
  const { t } = useTranslation();
  const baseClass = 'cancer-history-card';
  const hasCancers = pathologies.length > 0;
  const createDataTestId = () => `${t(relative ?? '')}${num ? '-' + num + '-' : '-'}${baseClass}`;

  return (
    <div className={baseClass} data-testid={createDataTestId()}>
      {relative === 'relatives.self' && !hasCancers && (
        <>
          <div className={`${baseClass}__container`}>
            <div className={`${baseClass}__relative-wrapper`} aria-label="Self cancer history">
              <Text1 className={`${baseClass}__title text-large--semi-bold`}>{t('relatives.self')}</Text1>
            </div>
            <div className={`${baseClass}__self-no-history`}>
              <Text1 className="text-medium--regular">{t('self.noHistory')}</Text1>
            </div>
          </div>
          <Button className={`${baseClass}__add ${isSimonMedUrl() ? baseClass + '__add--smi' : ''}`} dataTestId="add-pathology" onClick={onAddClicked}>
            <FontAwesomeIcon className={`${baseClass}__add-icon`} icon={faPlus} />
            <span className="text-medium--bold">{t('pathology.add')}</span>
          </Button>
        </>
      )}

      {relative !== null && hasCancers && (
        <>
          <div className={`${baseClass}__container`}>
            <div className={`${baseClass}__relative-wrapper`} aria-label={`${t(relative)} cancer history`}>
              <Text1 className={`${baseClass}__relative text-large--semi-bold`}>{`${t(relative)} ${num || ''}`}</Text1>
            </div>
            {pathologies.length > 0 &&
              pathologies.map((pathology, i) => (
                <PathologyDetails
                  key={i}
                  pathologyNum={i}
                  pathology={pathology}
                  onEditClicked={onEditClicked ? () => onEditClicked(pathology, i) : undefined}
                  onDeleteClicked={onDeleteClicked ? () => onDeleteClicked(pathology, i) : undefined}
                />
              ))}
          </div>
          <Button className={`${baseClass}__add ${isSimonMedUrl() ? baseClass + '__add--smi' : ''}`} dataTestId="add-pathology" onClick={onAddClicked}>
            <FontAwesomeIcon className={`${baseClass}__add-icon`} icon={faPlus} />
            <span className="text-medium--bold">{t('pathology.add')}</span>
          </Button>
        </>
      )}

      {relative === null && (
        <>
          <div className={`${baseClass}__container`}>
            <div className={`${baseClass}__relative-wrapper`} aria-label="Relative cancer history">
              <Text1 bold="true" className={`${baseClass}__title text-large--semi-bold`}>
                {t('relatives.bloodRelatives')}
              </Text1>
            </div>
            <div className={`${baseClass}__relative-no-history`}>
              <Text1 className="text-medium--regular">{t('relatives.noHistory')}</Text1>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default CancerHistoryCard;
